import {
  ChangeEvent,
  ComponentPropsWithoutRef,
  ReactElement,
  Ref,
  forwardRef,
  useId,
} from "react";
import { useTrackingId } from "../../hooks";
import {
  AiMarkWithTooltipOrPopoverProps,
  DataTrackingId,
  LayoutUtilProps,
} from "../../types";
import { childrenToString } from "../../internal/functions";
import { useLayoutPropsUtil } from "../../internal/hooks";
import Check from "@servicetitan/hammer-icon/mdi/round/check.svg";
import Icon from "../Icon";
import Flex from "../Flex";
import { Helper } from "../../internal/components";
import { AiMark } from "../../components/AiMark";

import cx from "classnames";
import styles from "./Switch.module.scss";

/**
 * State object returned by the Switch onChange callback
 */
export type SwitchState = {
  /**
   * Whether the switch is currently checked
   */
  checked: boolean;
  /**
   * The value of the switch input
   */
  value?: string | number | readonly string[];
};

/**
 * Props for the Switch component
 * @extends ComponentPropsWithoutRef<"input">
 * @extends LayoutUtilProps
 */
export type SwitchProps = Omit<ComponentPropsWithoutRef<"input">, "onChange"> &
  LayoutUtilProps & {
    /**
     * Callback when switch is changed.
     * @param e Change event object
     * @param state Current switch state with checked status and value
     */
    onChange?: (e: ChangeEvent<HTMLInputElement>, state: SwitchState) => void;

    /**
     * Label for the switch.
     * @remarks This should either be a string or have text content inside for accessibility
     */
    label?: ReactElement | string;

    /**
     * AI mark configuration to display next to the label.
     * Can be a boolean to show a simple AI mark, or an object with tooltip/popover configuration.
     */
    labelAiMark?: AiMarkWithTooltipOrPopoverProps["aiMark"];

    /**
     * Description text to display below the label
     */
    description?: string;
  } & DataTrackingId;

/**
 * Switch component for toggling between on/off states.
 *
 * Features:
 * - Toggle switch with smooth animations
 * - Check icon indicator when active
 * - Accessible with proper ARIA roles and attributes
 * - Keyboard navigation support (Space/Enter)
 * - Controlled and uncontrolled state management
 * - Customizable label and styling
 * - Layout utilities for positioning and spacing
 * - Focus management with visible focus indicators
 * - Hover states and visual feedback
 * - Disabled state support
 * - Automatic tracking ID generation for analytics
 *
 * @example
 * <Switch
 *   label="Enable notifications"
 *   onChange={(e, state) => console.log('Switch toggled:', state.checked)}
 * />
 */
export const Switch = forwardRef(
  (props: SwitchProps, ref: Ref<HTMLInputElement>) => {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      label,
      labelAiMark,
      description,
      className,
      onChange,
      checked,
      defaultChecked,
      value,
      style,
      "aria-label": ariaLabel,
      ...rest
    } = componentProps;

    const helperUid = useId();

    const onChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
      onChange?.(e, { value, checked: e.target.checked });
    };

    const data = {
      label: childrenToString(label),
      ariaLabel: ariaLabel || "Toggle option",
      ariaLabelledBy: rest["aria-labelledby"],
      value,
    };

    const trackingId = useTrackingId({
      name: "Switch",
      data,
      hasOverride: !!rest["data-tracking-id"],
    });

    const switchClassNames = cx(styles["switch"], className);

    const styleCombined = {
      ...style,
      ...layoutStyles,
      alignItems: description ? "flex-start" : "center",
    };

    return (
      <label className={switchClassNames} style={styleCombined}>
        <input
          data-tracking-id={trackingId}
          data-anv="switch"
          ref={ref}
          type="checkbox"
          role="switch"
          value={value}
          aria-checked={checked ?? defaultChecked}
          aria-label={label ? undefined : ariaLabel || "Toggle option"}
          checked={checked}
          defaultChecked={defaultChecked}
          onChange={onChangeHandler}
          {...rest}
        />
        <span aria-hidden className={styles["toggle"]}>
          <span className={styles["indicator"]}>
            <Icon svg={Check} className={styles["check"]} />
          </span>
        </span>
        {label || description ? (
          <Flex direction="column" gap={1}>
            <Flex alignItems="center" gap={2}>
              {label && label}
              {labelAiMark && (
                <AiMark
                  className={styles["ai-mark-icon"]}
                  type="gradient"
                  popoverOrTooltipConfig={
                    typeof labelAiMark === "object" ? labelAiMark : undefined
                  }
                />
              )}
            </Flex>
            {description && <Helper id={helperUid} description={description} />}
          </Flex>
        ) : null}
      </label>
    );
  },
);

Switch.displayName = "Switch";
